// "My HR": what every employee sees about themselves, plus the self-service
// dialogs (apply leave, fix a day, ask HR) that the quick-actions menu opens.
import React, { useEffect, useMemo, useState } from "react";
import { Button, CircularProgress, MenuItem, TextField } from "@mui/material";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined";
import CelebrationOutlinedIcon from "@mui/icons-material/CelebrationOutlined";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import MoreTimeOutlinedIcon from "@mui/icons-material/MoreTimeOutlined";
import EditCalendarOutlinedIcon from "@mui/icons-material/EditCalendarOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import {
  hr,
  fileUrl,
  fmtDate,
  fmtTime,
  fmtMinutes,
  days as daysText,
  thisMonth,
  monthLabel,
  COLORS,
  DOC_CATEGORIES,
  REQUEST_CATEGORIES,
  EMPLOYMENT_TYPES,
  GENDERS,
} from "./api";
import {
  useLoad,
  useAction,
  Loading,
  Card,
  Pill,
  Tag,
  Stat,
  Empty,
  DateBadge,
  FormDialog,
  ConfirmDialog,
  MonthNav,
  MonthCalendar,
  DayLegend,
  dayTone,
  dayLabel,
} from "./shared";
import styles from "./Hrms.module.css";

const tf = { size: "small", fullWidth: true };
const btn = { textTransform: "none", fontWeight: 700, fontFamily: "inherit", borderRadius: "10px" };

const dateRange = (r) =>
  `${fmtDate(r.start_date)}${r.end_date !== r.start_date ? ` – ${fmtDate(r.end_date)}` : ""}${r.half_day ? ` (${r.half_day} half)` : ""}`;

// ---------------------------------------------------------------- overview

function useClock(timeZone) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 15000);
    return () => clearInterval(t);
  }, []);
  return now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone });
}

export function useCheckInOut(me, onChanged) {
  const { busy, run } = useAction();
  const t = me.todayAttendance;
  const state = !t || !t.check_in ? "out" : !t.check_out ? "in" : "done";
  const act = () =>
    state === "out"
      ? run("/me/check-in", { method: "POST" }, onChanged)
      : state === "in"
        ? run("/me/check-out", { method: "POST" }, onChanged)
        : null;
  return { state, busy, act };
}

function CheckInCard({ me, onChanged }) {
  const tz = me.settings.timezone;
  const clock = useClock(tz);
  const { state, busy, act } = useCheckInOut(me, onChanged);
  const t = me.todayAttendance;
  const inAt = t && t.check_in;
  const outAt = t && t.check_out;
  const worked = inAt && !outAt ? Math.round((Date.now() - new Date(inAt)) / 60000) : (t && t.minutes_worked) || 0;
  const full = me.settings.full_day_minutes || 480;
  return (
    <div className={`${styles.card} ${styles.checkin}`}>
      <div className={styles.checkinTop}>
        <div>
          <div className={styles.eyebrow}>{fmtDate(me.today, { weekday: "long" })}</div>
          <div className={styles.clock}>{clock}</div>
          <div className={styles.muted}>
            Office hours {me.settings.work_start} – {me.settings.work_end}
          </div>
        </div>
        {state === "out" && (
          <Button
            variant="contained"
            color="success"
            className={styles.bigBtn}
            disabled={busy}
            startIcon={busy ? null : <LoginRoundedIcon />}
            onClick={act}
          >
            {busy ? <CircularProgress size={20} color="inherit" /> : "Check in"}
          </Button>
        )}
        {state === "in" && (
          <Button
            variant="contained"
            color="error"
            className={styles.bigBtn}
            disabled={busy}
            startIcon={busy ? null : <LogoutRoundedIcon />}
            onClick={act}
          >
            {busy ? <CircularProgress size={20} color="inherit" /> : "Check out"}
          </Button>
        )}
        {state === "done" && (
          <span className={styles.pill} style={{ background: "#ecfdf3", color: "#15803d", fontSize: 14, padding: "8px 14px" }}>
            <CheckCircleRoundedIcon sx={{ fontSize: 18 }} /> Done for today
          </span>
        )}
      </div>
      <div className={styles.timeStrip}>
        <div className={styles.timeCell}>
          <span>Check in</span>
          <b>{inAt ? fmtTime(inAt, tz) : "—"}</b>
        </div>
        <div className={styles.timeCell}>
          <span>Check out</span>
          <b>{outAt ? fmtTime(outAt, tz) : "—"}</b>
        </div>
        <div className={styles.timeCell}>
          <span>Worked</span>
          <b>{inAt ? fmtMinutes(worked) : "—"}</b>
        </div>
      </div>
      <div className={styles.progress} title={`${fmtMinutes(worked)} of ${fmtMinutes(full)}`}>
        <span style={{ width: `${Math.min(100, (worked / full) * 100)}%` }} />
      </div>
      <div className={styles.inline} style={{ marginTop: 8, justifyContent: "space-between" }}>
        <span className={styles.small}>
          {inAt ? `${Math.min(100, Math.round((worked / full) * 100))}% of a full day (${fmtMinutes(full)})` : "Check in when you start work."}
        </span>
        {t && t.late_minutes > 0 && <Pill status="late" label={`Late by ${fmtMinutes(t.late_minutes)}`} />}
      </div>
    </div>
  );
}

function ThisMonth({ me, goTo }) {
  const month = thisMonth(me.today);
  const { data } = useLoad(`/me/attendance?month=${month}`);
  const s = data && data.summary;
  return (
    <Card
      title={monthLabel(month)}
      icon={<TodayOutlinedIcon />}
      actions={
        <button type="button" className={styles.linkBtn} onClick={() => goTo("attendance")}>
          Attendance →
        </button>
      }
    >
      {!s ? (
        <CircularProgress size={18} />
      ) : (
        <div className={styles.tiles} style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
          <Stat label="Present" value={s.present} tone="good" icon={<TaskAltOutlinedIcon />} />
          <Stat label="Late arrivals" value={s.late} tone="warn" icon={<ScheduleOutlinedIcon />} />
          <Stat label="On leave" value={s.leave} tone="info" icon={<BeachAccessOutlinedIcon />} />
          <Stat label="Absent" value={s.absent} tone="bad" icon={<EventBusyOutlinedIcon />} />
        </div>
      )}
    </Card>
  );
}

// Leave balance cards, one colour per leave type.
export function Balances({ balances }) {
  return (
    <div className={styles.balances}>
      {balances.map((b, i) => {
        const total = b.entitlement + b.adjusted;
        const taken = b.used + b.pending;
        const pct = !b.unlimited && total ? Math.min(100, (taken / total) * 100) : 0;
        return (
          <div key={b.leave_type_id} className={`${styles.balance} ${styles[`c_${COLORS[i % COLORS.length]}`]}`}>
            <div className={styles.balanceTop}>
              <span className={styles.balanceName}>{b.name}</span>
              {b.code && <span className={styles.balanceCode}>{b.code}</span>}
            </div>
            <div className={styles.balanceValue}>
              {b.unlimited ? "∞" : Number(b.available)}
              <small>{b.unlimited ? "no limit" : Number(b.available) === 1 ? "day left" : "days left"}</small>
            </div>
            {!b.unlimited && (
              <div className={styles.bar}>
                <span style={{ width: `${pct}%` }} />
              </div>
            )}
            <div className={styles.balanceMeta}>
              {b.used} used
              {b.pending ? ` · ${b.pending} pending` : ""}
              {!b.unlimited ? ` · ${total} total` : ""}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function MyOverview({ me, onChanged, goTo, openDialog }) {
  const leave = useLoad("/me/leave");
  const upcoming = (leave.data?.requests || []).filter((r) => ["pending", "approved"].includes(r.status) && r.end_date >= me.today);
  const holidays = (leave.data?.holidays || []).filter((h) => h.date >= me.today).slice(0, 4);
  return (
    <>
      <div className={styles.gridMain}>
        <CheckInCard me={me} onChanged={onChanged} />
        <ThisMonth me={me} goTo={goTo} />
      </div>
      <Card
        title={`Leave balance${leave.data ? ` · ${leave.data.year}` : ""}`}
        icon={<BeachAccessOutlinedIcon />}
        actions={
          <Button className={styles.primaryBtn} variant="contained" startIcon={<AddRoundedIcon />} onClick={() => openDialog("leave")}>
            Apply for leave
          </Button>
        }
      >
        {leave.data ? <Balances balances={leave.data.balances} /> : <CircularProgress size={18} />}
      </Card>
      <div className={styles.grid2}>
        <Card title="Upcoming leave" icon={<EventAvailableOutlinedIcon />} color="blue">
          {upcoming.length ? (
            upcoming.map((r) => (
              <div key={r.id} className={styles.listRow}>
                <DateBadge date={r.start_date} color="blue" />
                <div className={styles.listMain}>
                  <div className={styles.listTitle}>{r.leave_type}</div>
                  <div className={styles.small}>
                    {dateRange(r)} · {daysText(r.days)}
                  </div>
                </div>
                <Pill status={r.status} />
              </div>
            ))
          ) : (
            <Empty icon={<EventAvailableOutlinedIcon />} text="No leave planned." />
          )}
        </Card>
        <Card title="Upcoming holidays" icon={<CelebrationOutlinedIcon />} color="pink">
          {holidays.length ? (
            holidays.map((h) => (
              <div key={h.id} className={styles.listRow}>
                <DateBadge date={h.date} color="pink" />
                <div className={styles.listMain}>
                  <div className={styles.listTitle}>{h.name}</div>
                  <div className={styles.small}>{fmtDate(h.date, { weekday: "long" })}</div>
                </div>
                {h.optional && <Tag color="slate">Optional</Tag>}
              </div>
            ))
          ) : (
            <Empty icon={<CelebrationOutlinedIcon />} text="No holidays added yet." />
          )}
        </Card>
      </div>
    </>
  );
}

// -------------------------------------------------------------- attendance

function dayLines(d, tz) {
  const lines = [];
  const label = dayLabel(d.status);
  if (d.check_in) lines.push(`${fmtTime(d.check_in, tz)}–${d.check_out ? fmtTime(d.check_out, tz) : "…"}`);
  if (d.minutes_worked) lines.push(fmtMinutes(d.minutes_worked));
  if (d.late_minutes > 0) lines.push(`Late ${fmtMinutes(d.late_minutes)}`);
  if (d.leave) lines.push(`${d.leave.code}${d.leave.half_day ? " (½)" : ""}`);
  if (d.holiday) lines.push(d.holiday);
  if (label && !d.check_in && !d.leave && !d.holiday) lines.push(label);
  if (d.source === "regularized") lines.push("Regularized");
  return lines;
}

export function attendanceCells(days, tz) {
  const cells = {};
  for (const d of days) {
    const tone = dayTone(d.status);
    cells[d.date] = { tone: tone === "blank" ? undefined : tone, lines: dayLines(d, tz) };
  }
  return cells;
}

export function AttendanceTiles({ s }) {
  return (
    <div className={styles.tiles}>
      <Stat label="Present" value={s.present} tone="good" icon={<TaskAltOutlinedIcon />} />
      <Stat label="Late arrivals" value={s.late} tone="warn" icon={<ScheduleOutlinedIcon />} />
      <Stat label="Leave" value={s.leave} tone="info" icon={<BeachAccessOutlinedIcon />} />
      <Stat label="Absent" value={s.absent} tone="bad" icon={<EventBusyOutlinedIcon />} />
      <Stat label="Average day" value={s.avg_minutes ? fmtMinutes(s.avg_minutes) : "—"} tone="violet" icon={<TimerOutlinedIcon />} />
      <Stat label="Overtime" value={s.overtime_minutes ? fmtMinutes(s.overtime_minutes) : "—"} tone="teal" icon={<MoreTimeOutlinedIcon />} />
    </div>
  );
}

export function RegularizeDialog({ me, open, onClose, onDone }) {
  const blank = { date: "", check_in: me.settings.work_start, check_out: me.settings.work_end, reason: "" };
  const [form, setForm] = useState(blank);
  const { busy, run } = useAction();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title="Fix a day"
      subtitle={`Forgot to check in or out? Ask for the right times — your manager or HR approves it. Last ${me.settings.regularization_window_days} days only.`}
      icon={<EditCalendarOutlinedIcon />}
      color="amber"
      actions={
        <Button
          className={styles.primaryBtn}
          variant="contained"
          disabled={busy || !form.date || !form.reason.trim()}
          onClick={() =>
            run("/me/regularizations", { method: "POST", body: form }, () => {
              setForm(blank);
              onDone();
            })
          }
        >
          Send request
        </Button>
      }
    >
      <div className={styles.formRow} style={{ marginTop: 8 }}>
        <TextField {...tf} type="date" label="Date" InputLabelProps={{ shrink: true }} value={form.date} onChange={set("date")} inputProps={{ max: me.today }} />
        <TextField {...tf} type="time" label="Check in" InputLabelProps={{ shrink: true }} value={form.check_in} onChange={set("check_in")} />
        <TextField {...tf} type="time" label="Check out" InputLabelProps={{ shrink: true }} value={form.check_out} onChange={set("check_out")} />
      </div>
      <TextField {...tf} multiline minRows={2} label="Reason" value={form.reason} onChange={set("reason")} />
    </FormDialog>
  );
}

export function MyAttendance({ me, openDialog }) {
  const [month, setMonth] = useState(thisMonth(me.today));
  const { data, error, reload } = useLoad(`/me/attendance?month=${month}`);
  const { run } = useAction();
  const tz = me.settings.timezone;
  if (!data) return <Loading error={error} />;
  return (
    <>
      <AttendanceTiles s={data.summary} />
      <Card
        title="My attendance"
        subtitle={`${data.summary.working_days} working days this month so far`}
        icon={<TodayOutlinedIcon />}
        actions={
          <>
            <MonthNav month={month} onChange={setMonth} />
            <Button variant="outlined" color="warning" startIcon={<EditCalendarOutlinedIcon />} onClick={() => openDialog("regularize")} sx={btn}>
              Fix a day
            </Button>
          </>
        }
      >
        <MonthCalendar month={month} cells={attendanceCells(data.days, tz)} today={me.today} />
        <DayLegend />
      </Card>
      <Card title="My fix requests" icon={<HistoryOutlinedIcon />} color="amber">
        {data.regularizations.length === 0 ? (
          <Empty icon={<EditCalendarOutlinedIcon />} text="No requests yet. Use “Fix a day” if you forgot to check in or out." />
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Times</th>
                  <th>Reason</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.regularizations.map((r) => (
                  <tr key={r.id}>
                    <td>{fmtDate(r.date)}</td>
                    <td>
                      {fmtTime(r.check_in, tz)} – {fmtTime(r.check_out, tz)}
                    </td>
                    <td className={styles.small}>
                      {r.reason}
                      {r.decision_note && <div>Note: {r.decision_note}</div>}
                    </td>
                    <td>
                      <Pill status={r.status} />
                    </td>
                    <td className={styles.rowActions}>
                      {r.status === "pending" && (
                        <Button size="small" color="error" onClick={() => run(`/me/regularizations/${r.id}/cancel`, { method: "POST" }, reload)} sx={btn}>
                          Cancel
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

// ------------------------------------------------------------------- leave

export function ApplyLeaveDialog({ open, onClose, onDone }) {
  const leave = useLoad(open ? "/me/leave" : null);
  const balances = leave.data?.balances || [];
  const blank = { leave_type_id: "", start_date: "", end_date: "", half_day: "", reason: "" };
  const [form, setForm] = useState(blank);
  const [count, setCount] = useState(null);
  const { busy, run } = useAction();
  const type = balances.find((b) => String(b.leave_type_id) === String(form.leave_type_id));
  const single = form.start_date && (!form.end_date || form.end_date === form.start_date);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!form.start_date) return setCount(null);
    let alive = true;
    const q = new URLSearchParams({ start_date: form.start_date, end_date: form.end_date || form.start_date, half_day: single ? form.half_day : "" });
    hr(`/me/leave/days?${q}`)
      .then(({ data }) => alive && setCount(data.days))
      .catch(() => alive && setCount(null));
    return () => {
      alive = false;
    };
  }, [form.start_date, form.end_date, form.half_day, single]);

  const over = type && !type.unlimited && count != null && count > type.available;
  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title="Apply for leave"
      subtitle="Weekly offs and holidays in the range aren't counted."
      icon={<BeachAccessOutlinedIcon />}
      color="blue"
      actions={
        <Button
          className={styles.primaryBtn}
          variant="contained"
          disabled={busy || !form.leave_type_id || !form.start_date || !count || over}
          onClick={() =>
            run(
              "/me/leave",
              { method: "POST", body: { ...form, end_date: form.end_date || form.start_date, half_day: single ? form.half_day : "" } },
              () => {
                setForm({ ...blank, leave_type_id: form.leave_type_id });
                onDone();
              },
            )
          }
        >
          {count ? `Apply for ${daysText(count)}` : "Apply"}
        </Button>
      }
    >
      <div className={styles.formRow} style={{ marginTop: 8 }}>
        <TextField {...tf} select label="Leave type" value={form.leave_type_id} onChange={set("leave_type_id")} className={styles.span2}>
          {balances.map((b) => (
            <MenuItem key={b.leave_type_id} value={b.leave_type_id}>
              {b.name} — {b.unlimited ? "no limit" : `${b.available} left`}
            </MenuItem>
          ))}
        </TextField>
        <TextField {...tf} type="date" label="From" InputLabelProps={{ shrink: true }} value={form.start_date} onChange={set("start_date")} />
        <TextField {...tf} type="date" label="To" InputLabelProps={{ shrink: true }} value={form.end_date} onChange={set("end_date")} inputProps={{ min: form.start_date }} />
        <TextField {...tf} select label="Duration" value={single ? form.half_day : ""} onChange={set("half_day")} disabled={!single || (type && !type.allow_half_day)} className={styles.span2}>
          <MenuItem value="">Full day</MenuItem>
          <MenuItem value="first">First half</MenuItem>
          <MenuItem value="second">Second half</MenuItem>
        </TextField>
      </div>
      <TextField {...tf} multiline minRows={2} label="Reason (optional)" value={form.reason} onChange={set("reason")} />
      {count != null && (
        <div className={over ? styles.warnBox : styles.infoBox} style={{ margin: "14px 0 0" }}>
          {over ? `That's ${daysText(count)} — more than the ${type.available} you have left.` : `${daysText(count)} of leave.`}
          {type && !type.unlimited && !over ? ` ${type.available - count} left after this.` : ""}
        </div>
      )}
    </FormDialog>
  );
}

export function LeaveCalendar({ me, title = "Leave calendar" }) {
  const [month, setMonth] = useState(thisMonth(me.today));
  const { data, error } = useLoad(`/calendar?month=${month}`);
  const cells = useMemo(() => {
    const out = {};
    if (!data) return out;
    const offs = new Set(data.weekly_offs);
    const [y, m] = month.split("-").map(Number);
    const n = new Date(Date.UTC(y, m, 0)).getUTCDate();
    for (let d = 1; d <= n; d++) {
      const date = `${month}-${String(d).padStart(2, "0")}`;
      if (offs.has(new Date(`${date}T00:00:00Z`).getUTCDay())) out[date] = { tone: "muted", lines: [] };
    }
    for (const h of data.holidays) out[h.date] = { tone: "muted", lines: [h.name] };
    for (const l of data.leave) {
      for (let d = l.start_date; d <= l.end_date; ) {
        if (d.startsWith(month)) {
          const c = out[d] || { tone: "info", lines: [] };
          if (c.tone !== "muted") c.tone = "info";
          c.lines.push(`${l.employee} · ${l.leave_type}${l.half_day ? " ½" : ""}${l.status === "pending" ? " (pending)" : ""}`);
          out[d] = c;
        }
        const nd = new Date(`${d}T00:00:00Z`);
        nd.setUTCDate(nd.getUTCDate() + 1);
        d = nd.toISOString().slice(0, 10);
      }
    }
    return out;
  }, [data, month]);
  return (
    <Card
      title={title}
      icon={<EventAvailableOutlinedIcon />}
      color="blue"
      subtitle={`${me.isHr ? "Everyone's leave." : me.isManager ? "Your leave and your team's." : "Your leave."} Striped days are weekly offs and holidays.`}
      actions={<MonthNav month={month} onChange={setMonth} />}
    >
      {error ? <div className={styles.errorBox}>{error}</div> : <MonthCalendar month={month} cells={cells} today={me.today} />}
    </Card>
  );
}

export function MyLeave({ me, openDialog }) {
  const { data, error, reload } = useLoad("/me/leave");
  const [cancelling, setCancelling] = useState(null);
  const { run } = useAction();
  if (!data) return <Loading error={error} />;
  return (
    <>
      <Card
        title="Leave balance"
        subtitle={`Leave year ${fmtDate(data.range.start)} – ${fmtDate(data.range.end)}`}
        icon={<BeachAccessOutlinedIcon />}
        actions={
          <Button className={styles.primaryBtn} variant="contained" startIcon={<AddRoundedIcon />} onClick={() => openDialog("leave")}>
            Apply for leave
          </Button>
        }
      >
        <Balances balances={data.balances} />
      </Card>
      <Card title="My leave requests" icon={<HistoryOutlinedIcon />} color="violet">
        {data.requests.length === 0 ? (
          <Empty icon={<BeachAccessOutlinedIcon />} title="No leave this year" text="Requests you make show up here with their status." />
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Dates</th>
                  <th>Type</th>
                  <th>Days</th>
                  <th>Approver</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.requests.map((r) => (
                  <tr key={r.id}>
                    <td>
                      {dateRange(r)}
                      {r.reason && <div className={styles.small}>{r.reason}</div>}
                    </td>
                    <td>{r.leave_type}</td>
                    <td>
                      <strong>{Number(r.days)}</strong>
                    </td>
                    <td className={styles.small}>{r.approver}</td>
                    <td>
                      <Pill status={r.status} />
                      {r.decision_note && <div className={styles.small}>{r.decision_note}</div>}
                    </td>
                    <td className={styles.rowActions}>
                      {r.status === "pending" && (
                        <Button size="small" color="error" onClick={() => setCancelling(r)} sx={btn}>
                          Cancel
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <LeaveCalendar me={me} />
      <Card title={`Holidays ${data.year}`} icon={<CelebrationOutlinedIcon />} color="pink">
        {data.holidays.length === 0 ? (
          <Empty icon={<CelebrationOutlinedIcon />} text="HR hasn't added holidays yet." />
        ) : (
          <div className={styles.peopleGrid}>
            {data.holidays.map((h) => (
              <div key={h.id} className={styles.person} style={{ opacity: h.date < me.today ? 0.5 : 1 }}>
                <DateBadge date={h.date} color="pink" />
                <div style={{ minWidth: 0 }}>
                  <div className={styles.personName}>{h.name}</div>
                  <div className={styles.personSub}>
                    {fmtDate(h.date, { weekday: "long", day: undefined, month: undefined, year: undefined })}
                    {h.optional ? " · optional" : ""}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      <ConfirmDialog
        open={Boolean(cancelling)}
        title="Cancel this leave request?"
        confirmLabel="Cancel request"
        danger
        onClose={() => setCancelling(null)}
        onConfirm={() => {
          const r = cancelling;
          setCancelling(null);
          run(`/me/leave/${r.id}/cancel`, { method: "POST" }, reload);
        }}
      >
        {cancelling && (
          <p>
            {cancelling.leave_type}, {dateRange(cancelling)}.
          </p>
        )}
      </ConfirmDialog>
    </>
  );
}

// --------------------------------------------------------------- documents

export function MyDocuments() {
  const { data, error } = useLoad("/me/documents");
  if (!data) return <Loading error={error} />;
  const payslips = data.documents.filter((d) => d.category === "payslip");
  const others = data.documents.filter((d) => d.category !== "payslip");
  return (
    <>
      <Card title="Payslips" icon={<ReceiptLongOutlinedIcon />} color="green" subtitle="Uploaded by HR each month. Click to download.">
        {payslips.length === 0 ? (
          <Empty icon={<ReceiptLongOutlinedIcon />} text="No payslips yet." />
        ) : (
          <div className={styles.docGrid}>
            {payslips.map((d) => (
              <a key={d.id} className={`${styles.docTile} ${styles.c_green}`} href={fileUrl(`/documents/${d.id}/download`)}>
                <span className={styles.tileIcon}>
                  <ReceiptLongOutlinedIcon />
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span className={styles.personName} style={{ display: "block" }}>
                    {d.period ? monthLabel(d.period) : d.title}
                  </span>
                  <span className={styles.personSub} style={{ display: "block" }}>
                    Added {fmtDate(d.createdAt)}
                  </span>
                </span>
                <FileDownloadOutlinedIcon sx={{ color: "#94a3b8" }} />
              </a>
            ))}
          </div>
        )}
      </Card>
      <Card title="My documents" icon={<DescriptionOutlinedIcon />} color="violet">
        {others.length === 0 ? (
          <Empty icon={<DescriptionOutlinedIcon />} text="Offer letter, ID proofs and other documents HR shares with you appear here." />
        ) : (
          <div className={styles.docGrid}>
            {others.map((d) => (
              <a key={d.id} className={`${styles.docTile} ${styles.c_violet}`} href={fileUrl(`/documents/${d.id}/download`)}>
                <span className={styles.tileIcon}>
                  <DescriptionOutlinedIcon />
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span className={styles.personName} style={{ display: "block" }}>
                    {d.title}
                  </span>
                  <span className={styles.personSub} style={{ display: "block" }}>
                    {DOC_CATEGORIES[d.category] || d.category} · {fmtDate(d.createdAt)}
                  </span>
                </span>
                <FileDownloadOutlinedIcon sx={{ color: "#94a3b8" }} />
              </a>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}

// ---------------------------------------------------------------- requests

const requestLabel = (c) => (REQUEST_CATEGORIES[c] || c).split(" (")[0];

export function AskHrDialog({ open, onClose, onDone }) {
  const blank = { category: "letter", subject: "", body: "" };
  const [form, setForm] = useState(blank);
  const { busy, run } = useAction();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title="Ask HR"
      subtitle="Letters, documents, salary questions, access — HR replies here."
      icon={<SupportAgentOutlinedIcon />}
      color="teal"
      actions={
        <Button
          className={styles.primaryBtn}
          variant="contained"
          disabled={busy || !form.subject.trim()}
          onClick={() =>
            run("/me/requests", { method: "POST", body: form }, () => {
              setForm(blank);
              onDone();
            })
          }
        >
          Send to HR
        </Button>
      }
    >
      <div className={styles.formRow} style={{ marginTop: 8 }}>
        <TextField {...tf} select label="About" value={form.category} onChange={set("category")}>
          {Object.entries(REQUEST_CATEGORIES).map(([k, v]) => (
            <MenuItem key={k} value={k}>
              {v}
            </MenuItem>
          ))}
        </TextField>
        <TextField {...tf} label="Subject" value={form.subject} onChange={set("subject")} />
      </div>
      <TextField {...tf} multiline minRows={4} label="Details" value={form.body} onChange={set("body")} />
    </FormDialog>
  );
}

export function MyRequests({ openDialog }) {
  const { data, error } = useLoad("/me/requests");
  if (!data) return <Loading error={error} />;
  return (
    <Card
      title="My requests to HR"
      icon={<SupportAgentOutlinedIcon />}
      color="teal"
      actions={
        <Button className={styles.primaryBtn} variant="contained" startIcon={<AddRoundedIcon />} onClick={() => openDialog("ask")}>
          New request
        </Button>
      }
    >
      {data.requests.length === 0 ? (
        <Empty icon={<SupportAgentOutlinedIcon />} title="Nothing asked yet" text="Need a letter, a document or help with salary? Send HR a request." />
      ) : (
        data.requests.map((r) => (
          <div key={r.id} className={styles.listRow} style={{ alignItems: "flex-start" }}>
            <span className={`${styles.tileIcon} ${styles.c_teal}`}>
              <SupportAgentOutlinedIcon />
            </span>
            <div className={styles.listMain}>
              <div className={styles.inline}>
                <span className={styles.listTitle}>{r.subject}</span>
                <Tag color="teal">{requestLabel(r.category)}</Tag>
              </div>
              {r.body && <div className={styles.muted}>{r.body}</div>}
              <div className={styles.small} style={{ marginTop: 2 }}>
                Sent {fmtDate(r.createdAt)}
              </div>
              {r.response && (
                <div className={styles.reply}>
                  <strong>HR:</strong> {r.response}
                </div>
              )}
            </div>
            <Pill status={r.status} />
          </div>
        ))
      )}
    </Card>
  );
}

// ----------------------------------------------------------------- profile

const SELF_FIELDS = [
  ["phone", "Phone"],
  ["personal_email", "Personal email"],
  ["date_of_birth", "Date of birth", "date"],
  ["gender", "Gender", "gender"],
  ["emergency_contact_name", "Emergency contact"],
  ["emergency_contact_phone", "Emergency contact phone"],
  ["address", "Address", "address"],
];

export function GenderField({ value, onChange, ...rest }) {
  const options = value && !GENDERS.includes(value) ? [...GENDERS, value] : GENDERS;
  return (
    <TextField {...tf} select label="Gender" value={value || ""} onChange={onChange} {...rest}>
      <MenuItem value="">—</MenuItem>
      {options.map((g) => (
        <MenuItem key={g} value={g}>
          {g}
        </MenuItem>
      ))}
    </TextField>
  );
}

export function MyProfile({ me, onChanged }) {
  const e = me.employee;
  const [form, setForm] = useState(() => Object.fromEntries(SELF_FIELDS.map(([k]) => [k, e[k] || ""])));
  const { busy, run } = useAction();
  const set = (k) => (ev) => setForm((f) => ({ ...f, [k]: ev.target.value }));
  return (
    <div className={styles.grid2}>
      <Card title="Job details" icon={<BadgeOutlinedIcon />} subtitle="Set by HR. If something is wrong, send HR a request.">
        <dl className={styles.kv}>
          <dt>Name</dt>
          <dd>
            {e.first_name} {e.last_name}
          </dd>
          <dt>Employee ID</dt>
          <dd>{e.employee_code}</dd>
          <dt>Work email</dt>
          <dd>{e.work_email}</dd>
          <dt>Designation</dt>
          <dd>{e.designation || "—"}</dd>
          <dt>Reports to</dt>
          <dd>{e.manager ? e.manager.name : "—"}</dd>
          <dt>Employment</dt>
          <dd>{EMPLOYMENT_TYPES[e.employment_type] || e.employment_type}</dd>
          <dt>Joined</dt>
          <dd>{fmtDate(e.joining_date)}</dd>
          {e.probation_end_date && (
            <>
              <dt>Probation ends</dt>
              <dd>{fmtDate(e.probation_end_date)}</dd>
            </>
          )}
          {e.ctc_annual != null && (
            <>
              <dt>Annual CTC</dt>
              <dd>₹{Number(e.ctc_annual).toLocaleString("en-IN")}</dd>
            </>
          )}
        </dl>
      </Card>
      <Card title="Personal details" icon={<PersonOutlineIcon />} color="violet" subtitle="Keep these up to date yourself.">
        <div className={styles.formRow}>
          {SELF_FIELDS.map(([k, label, type]) =>
            type === "gender" ? (
              <GenderField key={k} value={form[k]} onChange={set(k)} />
            ) : (
              <TextField
                key={k}
                {...tf}
                type={type === "date" ? "date" : "text"}
                label={label}
                InputLabelProps={type === "date" ? { shrink: true } : undefined}
                value={form[k]}
                onChange={set(k)}
                multiline={type === "address"}
                className={type === "address" ? styles.span2 : undefined}
              />
            ),
          )}
        </div>
        <Button className={styles.primaryBtn} variant="contained" disabled={busy} onClick={() => run("/me/profile", { method: "PUT", body: form }, onChanged)}>
          Save changes
        </Button>
      </Card>
    </div>
  );
}
